// @flow

import _         from 'lodash/fp';
import invariant from 'invariant';
import cuid      from 'cuid';

type Functor = Function; // todo
type ExpirationEntry = {
	cached: *,
	expireIn: number,
	startExpiration: number,
}
// validate

const validatePeriod = (rule: RegExp) => (period: string) => {
	const [, value, type] = period.match(rule);

	invariant(value, 'Value: `%value` is invalid!', value);
	invariant(type, 'Type: `%type` is invalid!', type);

	return [type, value];
};

// converter

const ONE_SECOND = 1000;
const ONE_MINUTE = ONE_SECOND * 60;

const converter = {
	's': (value: number) => ONE_SECOND * value,
	'm': (value: number) => ONE_MINUTE * value,
};

const convertToMilliseconds = ([type, value]): number => converter[type](value);

// exp

const entries: Map<string, ExpirationEntry> = new Map();


// eslint-disable-next-line no-unused-vars
const generateId = (): string => cuid();

const mapToCacheEntry = (value: *, startExpiration: number, expireIn: number): ExpirationEntry => ({
	cached: value,
	startExpiration,
	expireIn,
});

const updateEntry = (key: string, value: *, expireIn: number) => entries.set(key, mapToCacheEntry(
	value,
	Date.now(),
	expireIn
));

const notCreatedYet = (value: ExpirationEntry) => !value;
const isExpired = (value: ExpirationEntry) => Date.now() - value.startExpiration >= value.expireIn;

const isTimeUpdating = (value?: ExpirationEntry) => notCreatedYet(value) || isExpired(value);

const connectExpirationCore = (expireIn: number) => (name: string, ...callableArgs: *[]) => {
	const key = `${name} ${JSON.stringify(callableArgs)}`;

	const entryValue = entries.get(key);

	const updater = (callSourceCallback: Function) => {
		if (isTimeUpdating(entryValue)) {
			const result = callSourceCallback(...callableArgs); // args are bound

			updateEntry(key, result, expireIn);
			return result;
		}

		return entryValue.cached;
	};

	return [updater];
};

const expiration = _.compose(
	connectExpirationCore,
	convertToMilliseconds,
	validatePeriod(/\b(\d+)\s*(\w+)/)
);


// core

const connectDecorator = (module: Functor) => (target, prop, descriptor) => {
	const decorated = descriptor.value;

	descriptor.value = function (...args: *) {
		const name = prop || decorated.name || decorated.toString();

		const [updater] = module(name, ...args);
		const self = this;

		return updater((...args: *[]) => decorated.apply(self, args));
	};

	return descriptor;
};


const responsibleFor = (index: number, module: Functor) => (...args: *[]) => module(args[index]);

const FIRST_ARGUMENT = 0;

const cache = _.compose(
	connectDecorator,
	responsibleFor(FIRST_ARGUMENT, expiration),
);


export default cache;
